import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { Layout } from '../components/Layout';
import { Orb } from '../components/Orb';
import { api, ApiError } from '../lib/api';
import type { Me } from '@shared/types';

/** GET /admin/settings folds every settings row into a flat { key: value } map. */
type Settings = Record<string, string>;

/** GET /admin/vault — every API key/login the AI team uses, exact values. */
interface VaultItem {
  key: string;
  value: string | null;
  source: string;
  note: string;
}
interface VaultResponse {
  items: VaultItem[];
}

/** Friendly labels for the known hard-limit keys; unknown keys fall back to a title-cased key. */
const LIMIT_LABELS: Record<string, string> = {
  margin_floor: 'Margin floor',
  max_sends_per_profile_per_week: 'Max sends · profile · week',
  max_call_attempts: 'Max call attempts',
  max_split_ramp: 'Max split ramp %',
  call_window: 'Call window',
  nora_incentive: 'Nora incentive authority',
  seed_list_id: 'Seed list ID',
};

/** The canonical five safe-mode guards (design-system 04 §safe-mode strip). Phase A: all armed. */
const GUARDS = ['Klaviyo·seed', 'CheckoutChamp·sandbox', 'Voice·founders', 'Spend·caps', 'Schedules·HQ-cron'];

const labelFor = (k: string): string =>
  LIMIT_LABELS[k] ?? k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Mirror the Worker's rule: safe mode is ON unless the flag is explicitly off. */
const isSafeOn = (raw: string | undefined): boolean => !(raw === 'false' || raw === '0' || raw === 'off');

function Notice({ line }: { line: string }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '44px 20px' }}>
      <Orb agent="emma" size="md" />
      <p className="dim" style={{ marginTop: 14 }}>
        {line}
      </p>
    </div>
  );
}

const pwInput: CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  borderRadius: 'var(--r-ctl)',
  border: '1px solid var(--line-2)',
  background: 'var(--panel-2)',
  color: 'var(--text)',
  fontFamily: 'var(--font)',
  fontSize: 14,
};

/* ---------------- Credential Vault ---------------- */

/** Friendly labels for vault keys; unknown keys fall back to the raw key. */
const VAULT_LABELS: Record<string, string> = {
  ANTHROPIC_API_KEY: 'Anthropic API key',
  KLAVIYO_API_KEY: 'Klaviyo API key',
  ELEVENLABS_API_KEY: 'ElevenLabs API key',
  CHECKOUTCHAMP_LOGIN_ID: 'CheckoutChamp API login ID',
  CHECKOUTCHAMP_PASSWORD: 'CheckoutChamp API password',
  CHECKOUTCHAMP_ADMIN_USER: 'CheckoutChamp admin user',
  CHECKOUTCHAMP_ADMIN_PASSWORD: 'CheckoutChamp admin password',
  AI_TEAM_GMAIL_USER: 'AI-team Gmail user',
  AI_TEAM_GMAIL_PASSWORD: 'AI-team Gmail password',
  RUNNER_TOKEN: 'Runner token',
};

/** Masked form: bullets + the last 4 characters, so a founder can eyeball which key it is. */
const maskValue = (v: string): string => '••••••••' + v.slice(-4);

function EyeIcon({ off }: { off?: boolean }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {off ? (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </>
      ) : (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      )}
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

const vaultRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  padding: '12px 0',
  borderTop: '1px solid var(--line)',
};

function VaultRow({ item }: { item: VaultItem }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  function copy() {
    if (!item.value) return;
    navigator.clipboard
      .writeText(item.value)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        /* clipboard unavailable — nothing sensible to do */
      });
  }

  return (
    <div style={vaultRow}>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <div style={{ fontWeight: 550, fontSize: 13.5 }}>{VAULT_LABELS[item.key] ?? item.key}</div>
        <p className="small" style={{ marginTop: 2 }}>
          {item.source} · {item.note}
        </p>
      </div>
      <div
        className="mono"
        style={{ flex: '2 1 240px', minWidth: 0, fontSize: 12.5, wordBreak: 'break-all', color: item.value ? 'var(--text)' : 'var(--faint)' }}
      >
        {item.value ? (revealed ? item.value : maskValue(item.value)) : 'not set'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button
          type="button"
          className="btn btn-line sm"
          onClick={() => setRevealed((r) => !r)}
          disabled={!item.value}
          title={revealed ? 'Hide value' : 'Reveal value'}
          aria-label={revealed ? `Hide ${item.key}` : `Reveal ${item.key}`}
        >
          <EyeIcon off={revealed} />
        </button>
        <button
          type="button"
          className="btn btn-line sm"
          onClick={copy}
          disabled={!item.value}
          title="Copy value"
          style={copied ? { color: 'var(--mint)', borderColor: 'rgba(var(--mint-rgb),.4)' } : undefined}
        >
          {copied ? (
            <>
              <CheckIcon />
              Copied
            </>
          ) : (
            'Copy'
          )}
        </button>
      </div>
    </div>
  );
}

function CredentialVault({ vault }: { vault: VaultResponse | null }) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h3>Credential Vault</h3>
        <span className="chip chip-coral"><i />founders only</span>
      </div>
      <p className="small" style={{ marginBottom: 12, color: 'var(--amber)' }}>
        Full keys. Treat this page like a password manager — founders only.
      </p>

      {vault === null ? (
        <p className="dim small">Couldn't reach the vault just now — refresh to try again.</p>
      ) : (
        <div>
          {vault.items.map((item) => (
            <VaultRow key={item.key} item={item} />
          ))}

          {/* HQ logins — static; passwords are hashed server-side, so there's nothing to reveal. */}
          <div style={vaultRow}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ fontWeight: 550, fontSize: 13.5 }}>HQ logins</div>
              <p className="small" style={{ marginTop: 2 }}>
                HQ · passwords are hashed server-side; default = username until changed in Settings
              </p>
            </div>
            <div className="mono" style={{ flex: '2 1 240px', minWidth: 0, fontSize: 12.5 }}>
              zarg / gulfam / ishy
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!current || !next) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/auth/change-password', { current, next });
      setMsg({ ok: true, text: 'Password changed.' });
      setCurrent('');
      setNext('');
    } catch (err) {
      const text =
        err instanceof ApiError && err.status === 401
          ? 'Current password is wrong.'
          : err instanceof ApiError && err.status === 400
            ? 'New password too short (min 4).'
            : 'Something went wrong.';
      setMsg({ ok: false, text });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h3>Change your password</h3>
        <span className="chip chip-ice"><i />you</span>
      </div>
      <p className="small" style={{ marginBottom: 14 }}>
        Your default password is your username. Set your own here (min 4 characters).
      </p>
      <div className="grid g2">
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" autoComplete="current-password" style={pwInput} />
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password" autoComplete="new-password" style={pwInput} />
      </div>
      {msg && <p style={{ fontSize: 13, marginTop: 12, color: msg.ok ? 'var(--mint)' : 'var(--coral)' }}>{msg.text}</p>}
      <button className="btn btn-primary sm" disabled={busy} style={{ marginTop: 14 }}>
        {busy ? 'Saving…' : 'Update password'}
      </button>
    </form>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [vault, setVault] = useState<VaultResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get<Settings>('/admin/settings'),
      api.get<Me>('/auth/me'),
      // The vault failing (e.g. Worker not redeployed yet) must not take down the page.
      api.get<VaultResponse>('/admin/vault').catch(() => null),
    ])
      .then(([s, m, v]) => {
        if (!alive) return;
        setSettings(s);
        setMe(m);
        setVault(v && Array.isArray(v.items) ? v : null);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const readOnly = <span className="chip chip-dim"><i />read-only</span>;

  if (loading) {
    return (
      <Layout title="Settings" actions={readOnly}>
        <Notice line="Reading the house rules…" />
      </Layout>
    );
  }

  if (error || !settings) {
    return (
      <Layout title="Settings" actions={readOnly}>
        <Notice line="Couldn't reach settings just now. The config's still safe — try again in a moment." />
      </Layout>
    );
  }

  const safeOn = isSafeOn(settings.safe_mode);
  const limits = Object.entries(settings).filter(([k]) => k !== 'safe_mode');

  return (
    <Layout title="Settings" actions={readOnly}>
      <div className="grid" style={{ gap: 16 }}>
        {/* Credential Vault — every key/login the AI team uses, exact values */}
        <CredentialVault vault={vault} />

        {/* Safe-mode status */}
        <div className="card">
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}
          >
            <div>
              <div className="t-label">Safe mode</div>
              <div
                className="t-gauge"
                style={{ fontSize: 44, marginTop: 6, color: safeOn ? 'var(--mint)' : 'var(--coral)' }}
              >
                {safeOn ? 'ON' : 'OFF'}
              </div>
            </div>
            <span className={safeOn ? 'chip chip-mint' : 'chip chip-coral alarm'}>
              <i />
              {safeOn ? 'Armed' : 'Disabled'}
            </span>
          </div>

          {safeOn ? (
            <>
              <div className="guards">
                {GUARDS.map((g) => (
                  <span key={g} className="guard">
                    <i />
                    {g}
                  </span>
                ))}
              </div>
              <p className="small" style={{ marginTop: 12 }}>
                Every external call routes through safe mode. In Phase A all five guards stay armed — no exceptions.
              </p>
            </>
          ) : (
            <p className="small" style={{ marginTop: 12, color: 'var(--coral)' }}>
              Safe mode is off — external calls are no longer guarded. This should never be the case in Phase A.
            </p>
          )}
        </div>

        <div className="grid g2">
          {/* Hard limits — read-only config mirror */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h3>Hard limits</h3>
              <span className="chip chip-dim"><i />config</span>
            </div>
            <p className="small" style={{ marginBottom: 14 }}>
              These are config. Agents can read them, but never write.
            </p>
            {limits.length === 0 ? (
              <p className="dim small">No limits on file.</p>
            ) : (
              <table className="ledger">
                <thead>
                  <tr>
                    <th>Limit</th>
                    <th className="r">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {limits.map(([k, v]) => (
                    <tr key={k}>
                      <td>{labelFor(k)}</td>
                      <td className="r">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Founder access — allowlist; /auth/me only ever shows yourself */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <h3>Founder access</h3>
              <span className="chip chip-ice"><i />founders only</span>
            </div>
            <p className="small" style={{ marginBottom: 14 }}>
              Username + password, allowlisted. No public signup.
            </p>
            <div className="feed-item">
              <Orb agent="emma" size="md" />
              <div style={{ minWidth: 0 }}>
                <div className="who">{me?.name || 'You'}</div>
                <p className="mono" style={{ fontSize: 12.5, color: 'var(--dim)', marginTop: 4, wordBreak: 'break-all' }}>
                  {me?.email ?? '—'}
                </p>
                <div className="receipt">
                  You're seeing your own entry. The full allowlist lives in config, not the UI.
                </div>
              </div>
            </div>
          </div>
        </div>

        <ChangePassword />
      </div>
    </Layout>
  );
}
